import React, { useRef } from 'react';
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { ArrowUpRightIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
type Props = {
  slug: string;
  title: string;
  meta: string;
  image: string;
  tags: string[];
};

/** One project tile: an image with a minimal caption directly beneath it. Cards stay in place; the photo drifts inside its frame. */
export function WorkCard({
  slug,
  title,
  meta,
  image,
  tags
}: Props) {
  const ref = useRef<HTMLLIElement>(null);
  const reduce = useReducedMotion();
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ['start end', 'end start']
  });
  // Strong drift of the photo inside its frame
  const imgY = useTransform(scrollYProgress, [0, 1], reduce ? ['0%', '0%'] : ['-14%', '14%']);
  return <li ref={ref} data-id="element-403" data-mp="3ntcluebd93da">
      <div data-id="element-404" data-mp="3nu4fvkpbet1g">
        <Link to={`/work/${slug}`} className="group block focus:outline-none" data-id="element-405" data-mp="3nue5v8ad1075">
          <div className="relative aspect-[3/2] overflow-hidden bg-fg/5 group-focus-visible:ring-2 group-focus-visible:ring-fg" data-id="element-406" data-mp="3rtwhfdgajyxb">
            <motion.div style={{
            y: imgY
          }} className="absolute inset-x-0 -inset-y-[18%] will-change-transform" data-id="element-407" data-mp="38srnhwrwjpnl">
              <img src={image} alt={`${title} project`} loading="lazy" className="h-full w-full object-cover" data-id="element-408" data-mp="3iouogzb0pi8y" />
            </motion.div>
          </div>

          <div className="mt-4 flex items-start justify-between gap-6" data-id="element-409" data-mp="3dr2wk5adhw4r">
            <div className="min-w-0" data-id="element-410" data-mp="2pznodvvx0kll">
              <h3 className="text-[21px] leading-[1.25] tracking-[-0.01em] text-fg md:text-[24px]" data-id="element-411" data-mp="2q0p6gkbcm7ct">{title}</h3>
              <p className="mt-1.5 line-clamp-2 max-w-[460px] text-[15px] leading-[1.5] text-fg/50 md:text-[16px]" data-id="element-412" data-mp="2kyzpyp5cx1hc">{meta}</p>
              {tags.length > 0 && <ul className="mt-3 flex flex-wrap items-center gap-x-2 text-[12px] uppercase tracking-[0.04em] text-fg/40" data-id="element-413" data-mp="2uy8rc32jxidq">
                  {tags.map((tag, i) => <li key={tag} className="flex items-center gap-x-2" data-id="element-414" data-mp="22aupprnwryna" data-mpi={i}>
                      {i > 0 && <span className="h-2.5 w-px bg-fg/25" aria-hidden="true" data-id="element-415" data-mp="2ryqfn2owz9vw" />}
                      {tag}
                    </li>)}
                </ul>}
            </div>
            <ArrowUpRightIcon className="mt-1 h-5 w-5 shrink-0 text-fg/50 transition-[transform,color] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-fg" aria-hidden="true" data-id="element-416" data-mp="30r22yfoqeh7n" />
          </div>
        </Link>
      </div>
    </li>;
}